import { useEffect } from "react";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router";

/**
 * "← Back" goes to the page you came from (Front desk, Calendar, a client, Pending Charts…), named
 * after it. Opened directly (a link, a reload), it goes to the page's usual parent instead.
 */

/** The in-app pages visited, oldest first (this tab, since the app loaded). */
const trail: { key: string; path: string }[] = [];
/** Page names set by pages whose name isn't in the path (a client's name, an event's). */
const titles = new Map<string, string>();

const PATH_NAMES: [RegExp, string][] = [
  [/^\/$/, "Today"],
  [/^\/calendar/, "Calendar"],
  [/^\/front-desk/, "Front desk"],
  [/^\/communication/, "Communication"],
  [/^\/mobile-requests/, "Mobile requests"],
  [/^\/events\/check-in/, "Event check-in"],
  [/^\/events/, "Events"],
  [/^\/clients\/import/, "Import past visits"],
  [/^\/clients\/[^/]+/, "Client"],
  [/^\/clients/, "Clients"],
  [/^\/reports/, "Reports"],
  [/^\/payouts/, "Payouts"],
  [/^\/checkout/, "Checkout"],
  [/^\/pending-charts/, "Pending charts"],
  [/^\/visits/, "Visit"],
  [/^\/charts/, "Chart"],
  [/^\/records/, "Boulevard record"],
  [/^\/inventory/, "Inventory"],
  [/^\/availability/, "Availability"],
  [/^\/catalog/, "Catalog"],
  [/^\/staff/, "Staff"],
  [/^\/settings/, "Settings"],
  [/^\/audit/, "Audit log"],
];

/**
 * Keeps the trail (called once, in the app's frame). Updated while rendering, not in an effect, so
 * the page being shown already sees itself on it (the frame renders before the page); it's safe to
 * run twice for the same page.
 */
export function useBackTrail() {
  const loc = useLocation();
  const type = useNavigationType();
  const at = trail.findIndex((t) => t.key === loc.key);
  const entry = { key: loc.key, path: loc.pathname + loc.search };
  if (at >= 0) trail.splice(at + 1);
  else if (type === "REPLACE" && trail.length) trail[trail.length - 1] = entry;
  else trail.push(entry);
}

/** Names this page for the back link of the next one (e.g. the client's name). */
export function useBackTitle(title: string | null | undefined) {
  const { key } = useLocation();
  useEffect(() => {
    if (title) titles.set(key, title);
  }, [key, title]);
}

export function BackLink({ to, label, className = "text-sm font-semibold text-muted hover:text-ink" }: { to: string; label: string; className?: string }) {
  const nav = useNavigate();
  const { key } = useLocation();
  const here = trail.findIndex((t) => t.key === key);
  const prev = here > 0 ? trail[here - 1] : null;
  if (!prev) {
    return (
      <Link to={to} className={className}>
        ← {label}
      </Link>
    );
  }
  const name = titles.get(prev.key) ?? PATH_NAMES.find(([re]) => re.test(prev.path))?.[1] ?? "Back";
  return (
    <button type="button" onClick={() => nav(-1)} className={`self-start text-left ${className}`}>
      ← {name}
    </button>
  );
}
